import { Button } from "@ui/Button";
import { formatShortDate } from "@ui/format";
import { Loader } from "@ui/Loader";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Pill } from "@ui/Pill";
import { HStack, VStack } from "@ui/Stack";
import { MonoText } from "@ui/typography/MonoText";
import { Text } from "@ui/typography/Text";
import {
	createEffect,
	createMemo,
	createSignal,
	For,
	onCleanup,
	Show,
	untrack,
} from "solid-js";
import { createStore } from "solid-js/store";
import {
	type ArtifactListResponse,
	type ArtifactListResponseItem,
	listArtifacts,
} from "@/client";
import { KIND_CONFIG } from "~/modules/askgvt/artifacts/utils/itemKind";
import type { QuestionContextAnswer } from "./QuestionContext";
import type { QuestionContextListProps } from "./questionContextKeyboard";
import { isRecentAnswerContext } from "./RecentAnswerContexts";
import "./QuestionContextAnswers.css";

const PAGE_SIZE = 20;

/** How long the typed handle must stand still before it is searched for. */
const SEARCH_DEBOUNCE_MS = 250;

/** What the answers list calls and marks a kind of answer with. */
const kindConfig = (kind: ArtifactListResponseItem["kind"]) =>
	KIND_CONFIG[kind ?? "other"] ?? KIND_CONFIG.other;

const titleLabel = (item: ArtifactListResponseItem) =>
	item.title?.trim() ? item.title : "Untitled Answer";

const dateLabel = (createdAt: string | undefined) => {
	if (!createdAt) return null;
	const date = new Date(createdAt);
	return Number.isNaN(date.getTime()) ? null : formatShortDate(date);
};

const fetchAnswersPage = async (params: {
	offset: number;
	query?: string;
	questionId?: string;
}): Promise<ArtifactListResponse> => {
	try {
		const response = await listArtifacts({
			query: {
				limit: PAGE_SIZE,
				offset: params.offset,
				query: params.query || undefined,
				question_id: params.questionId,
				favourite: true,
			},
		});
		const data = response.data;
		if (data != null && "items" in data) {
			return {
				items: data.items ?? [],
				total: data.total ?? 0,
				limit: data.limit,
				offset: data.offset,
			};
		}
	} catch {
		// Keep the list usable if a page request fails.
	}
	return { items: [], total: 0 };
};

type Props = QuestionContextListProps & {
	/** The `@handle` being written, to search your answers for, or "" for none. */
	searchQuery: string;
	/**
	 * Whether this is the tab on screen. Answers are asked for only once it is,
	 * and what was found stays while it isn't: a tab nobody is looking at should
	 * neither fetch nor throw away a list.
	 */
	active: boolean;
	/** Question the conversation is, whose own answers are offered first. */
	contextQuestionId?: string;
	addedAnswerIds: () => string[];
	onAddAnswer: (answer: QuestionContextAnswer) => void;
	canAddMore?: () => boolean;
};

type QueryCache = {
	items: ArtifactListResponseItem[];
	total: number;
};

const AnswerRow = (props: {
	item: ArtifactListResponseItem;
	subtitle: string;
	/** Whether this answer belongs to the conversation the question is part of. */
	fromConversation: boolean;
	added: boolean;
	blocked: boolean;
	highlighted: boolean;
	onSelect: () => void;
}) => {
	let rowRef: HTMLDivElement | undefined;
	const config = () => kindConfig(props.item.kind);

	createEffect(() => {
		if (props.highlighted) rowRef?.scrollIntoView({ block: "nearest" });
	});

	return (
		<div
			ref={rowRef}
			class="gvtui-question-context-answers__item"
			classList={{
				"gvtui-question-context-answers__item--highlighted": props.highlighted,
				"gvtui-question-context-answers__item--blocked": props.blocked,
			}}
			role="button"
			tabIndex={props.highlighted ? 0 : -1}
			aria-disabled={props.blocked || undefined}
			aria-label={
				props.added
					? `${titleLabel(props.item)} already in context`
					: props.blocked
						? "Can't add more answers"
						: `Add ${titleLabel(props.item)}`
			}
			onMouseDown={(event) => event.preventDefault()}
			onClick={() => {
				if (!props.blocked) {
					props.onSelect();
				}
			}}
			onKeyDown={(event) => {
				if (props.blocked) {
					return;
				}
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					props.onSelect();
				}
			}}
		>
			<HStack fullWidth align="center" justify="space-between" spacing="medium">
				<HStack grow align="center" spacing="medium">
					{/*
					 * The kind's own colour, as the answers list marks it with — but
					 * drawn in it here, over a wash of it mixed into the panel's own
					 * surface, rather than filled with it. The wash strengthens with the
					 * row's highlight, so the row the keys are on carries more of its
					 * colour.
					 */}
					<MaterialSymbol
						symbol={config().symbol}
						style={{ color: config().colour }}
						container={{
							size: "2.25rem",
							borderRadius: "var(--box-radius-standard)",
							background: `color-mix(in oklch, ${config().colour} ${props.highlighted ? 45 : 30}%, var(--question-context-surface))`,
						}}
					/>
					<VStack grow>
						<Text color="inherit" ellipsis weight="bold">
							{titleLabel(props.item)}
						</Text>
						<HStack align="center" spacing="small">
							<MonoText color="fg3">{props.subtitle}</MonoText>
							<Show when={props.fromConversation}>
							<Pill theme="grey600" size="xsmall" variant="outline">
									This conversation
								</Pill>
							</Show>
						</HStack>
					</VStack>
				</HStack>
				<MaterialSymbol
					symbol={props.added ? "check" : "add"}
					color={props.added ? "success" : "fg3"}
					relativeSize="larger"
				/>
			</HStack>
		</div>
	);
};

export const QuestionContextAnswers = (props: Props) => {
	const [moreItems, setMoreItems] = createSignal<ArtifactListResponseItem[]>([]);
	const [loadingMore, setLoadingMore] = createSignal(false);
	const [searchedQuery, setSearchedQuery] = createSignal("");
	const [hasBeenShown, setHasBeenShown] = createSignal(false);
	const [loadingInitial, setLoadingInitial] = createSignal(false);
	const [byQuery, setByQuery] = createStore<Record<string, QueryCache>>({});
	const [conversationByQuestionId, setConversationByQuestionId] = createStore<
		Record<string, ArtifactListResponseItem[]>
	>({});
	let searchTimer: ReturnType<typeof setTimeout> | undefined;

	// While this is the tab on screen, the handle is searched for once it has
	// stood still for a moment; losing it takes effect at once, there being
	// nothing left to wait for. The first search of a visit doesn't wait either,
	// the handle having stood still all the while the user was elsewhere.
	createEffect(() => {
		if (!props.active) return;
		const query = props.searchQuery.trim();
		const firstVisit = !untrack(hasBeenShown);
		clearTimeout(searchTimer);
		if (query !== untrack(searchedQuery)) {
			if (firstVisit || query.length === 0) {
				setSearchedQuery(query);
			} else {
				searchTimer = setTimeout(
					() => setSearchedQuery(query),
					SEARCH_DEBOUNCE_MS,
				);
			}
		}
		// Last, so that the query to ask about is in place before asking begins.
		setHasBeenShown(true);
	});

	onCleanup(() => clearTimeout(searchTimer));

	const searchSource = createMemo(() => {
		if (!hasBeenShown()) return null;
		return { query: searchedQuery() };
	});

	createEffect(() => {
		if (!hasBeenShown()) return;
		const questionId = props.contextQuestionId?.trim();
		if (!questionId || conversationByQuestionId[questionId]) return;

		void fetchAnswersPage({ offset: 0, questionId }).then((page) => {
			if (props.contextQuestionId?.trim() !== questionId) return;
			setConversationByQuestionId(questionId, page.items ?? []);
		});
	});

	createEffect(() => {
		const source = searchSource();
		if (!source) return;
		const query = source.query;
		if (byQuery[query]) return;

		setLoadingInitial(true);
		void fetchAnswersPage({ offset: 0, query })
			.then((page) => {
				if (query !== searchedQuery()) return;
				setByQuery(query, {
					items: page.items ?? [],
					total: page.total ?? 0,
				});
			})
			.finally(() => {
				if (query === searchedQuery()) {
					setLoadingInitial(false);
				}
			});
	});

	// A new search is a new list, so the pages loaded for the last one go.
	createEffect(() => {
		const query = searchedQuery();
		setMoreItems([]);
	});

	const globalItems = (): ArtifactListResponseItem[] => [
		...(byQuery[searchedQuery()]?.items ?? []),
		...moreItems(),
	];

	const total = () => {
		const query = searchedQuery();
		const bucket = byQuery[query];
		if (!bucket) return 0;
		return bucket.total;
	};

	const isFromConversation = (item: ArtifactListResponseItem) =>
		props.contextQuestionId != null &&
		item.question_id === props.contextQuestionId;

	const conversationMatches = (): ArtifactListResponseItem[] => {
		const questionId = props.contextQuestionId?.trim();
		const items = questionId ? (conversationByQuestionId[questionId] ?? []) : [];
		const query = props.searchQuery.trim().toLowerCase();
		if (query.length === 0) return items;
		return items.filter((item) =>
			`${item.title ?? ""} ${item.summary ?? ""}`.toLowerCase().includes(query),
		);
	};

	/**
	 * This conversation's own answers first, then the rest of yours. An answer
	 * can be reached both ways, so the second time it is passed over.
	 */
	const rows = createMemo<ArtifactListResponseItem[]>(() => {
		const items = [...conversationMatches()];
		const seen = new Set(
			items
				.map((item) => item.public_id)
				.filter((id): id is string => Boolean(id)),
		);
		for (const item of globalItems()) {
			if (item.public_id) {
				if (seen.has(item.public_id)) continue;
				seen.add(item.public_id);
			}
			items.push(item);
		}
		return items;
	});

	const subtitle = (item: ArtifactListResponseItem) => {
		const parts = [kindConfig(item.kind).label];
		const date = dateLabel(item.created_at);
		if (date) {
			parts.push(date);
		}
		if (item.public_id && isRecentAnswerContext(item.public_id)) {
			parts.push("Recent");
		}
		return parts.join(" · ");
	};

	// Counted over the answers the pages themselves hold: the conversation's own
	// answers are not part of that list and must not shift its next offset.
	const hasMore = () => globalItems().length < total();

	const isAdded = (item: ArtifactListResponseItem) => {
		const id = item.public_id;
		return id != null && props.addedAnswerIds().includes(id);
	};

	const toContextAnswer = (
		item: ArtifactListResponseItem,
	): QuestionContextAnswer | null => {
		const id = item.public_id?.trim();
		const artifactId = item.artifact_id?.trim();
		if (!id || !artifactId) {
			return null;
		}
		const description =
			item.summary?.trim() || item.title?.trim() || undefined;
		return {
			id,
			title: titleLabel(item),
			description,
			artifactId,
		};
	};

	const canAddMore = () => props.canAddMore?.() ?? true;

	const addAnswer = (item: ArtifactListResponseItem) => {
		if (isAdded(item) || !canAddMore()) {
			return;
		}
		const answer = toContextAnswer(item);
		if (answer == null) {
			return;
		}
		props.onAddAnswer(answer);
	};

	props.registerList({
		count: () => rows().length,
		select: (index) => {
			const item = rows()[index];
			if (item) addAnswer(item);
		},
	});

	const loadMore = async () => {
		if (loadingMore() || !hasMore()) {
			return;
		}
		const query = searchedQuery();
		setLoadingMore(true);
		try {
			const page = await fetchAnswersPage({
				offset: globalItems().length,
				query,
			});
			// The handle may have moved on while the page was loading, in which case
			// these answers belong to a list that is no longer on screen.
			if (query !== searchedQuery()) {
				return;
			}
			const nextItems = page.items ?? [];
			if (nextItems.length === 0) {
				setByQuery(query, (prev) => ({
					items: prev?.items ?? [],
					total: globalItems().length,
				}));
				return;
			}
			setMoreItems((prev) => [...prev, ...nextItems]);
			setByQuery(query, (prev) => ({
				items: prev?.items ?? [],
				total: page.total ?? 0,
			}));
		} finally {
			setLoadingMore(false);
		}
	};

	return (
		<Show
			when={rows().length > 0 || !loadingInitial()}
			fallback={
				<HStack fullWidth justify="center">
					<Loader />
				</HStack>
			}
		>
			<VStack fullWidth spacing="none">
				<For each={rows()}>
					{(item, index) => (
						<AnswerRow
							item={item}
							subtitle={subtitle(item)}
							fromConversation={isFromConversation(item)}
							added={isAdded(item)}
							blocked={!isAdded(item) && !canAddMore()}
							highlighted={index() === props.highlightedIndex}
							onSelect={() => addAnswer(item)}
						/>
					)}
				</For>
				<Show when={searchedQuery().length > 0 && rows().length === 0}>
					<HStack fullWidth justify="center">
						<Text color="fg3">No answers match</Text>
					</HStack>
				</Show>
				<Show when={hasMore()}>
					<HStack
						class="gvtui-question-context-answers__item"
						fullWidth
						justify="center"
					>
						<Button
							variant="text"
							symbol={loadingMore() ? undefined : "expand_more"}
							disabled={loadingMore()}
							aria-busy={loadingMore()}
							aria-label="Show more answers"
							onMouseDown={(event) => event.preventDefault()}
							onClick={() => void loadMore()}
						>
							<Show when={loadingMore()}>
								<Loader size="xsmall" />
							</Show>
							Show more
						</Button>
					</HStack>
				</Show>
			</VStack>
		</Show>
	);
};
